/**
 * Open routing: how a node finds the network, and how the network heals
 * when someone tries to remove it.
 *
 * The design assumption is adversarial and not hypothetical: at some point
 * a domain gets seized, an ASN gets null-routed, an app store delists, a
 * cloud provider receives a sanctions notice. A network positioned as
 * banking infrastructure for people whom banks have already excluded has
 * to keep working through all of that, which means no single name, host,
 * company or country may be load-bearing.
 *
 * Five properties get us there:
 *
 *  1. Relays are interchangeable and blind. A relay forwards sealed frames
 *     between peers (see noise.ts). It holds no keys, no balances and no
 *     authority; it cannot read or forge anything. Losing one costs the
 *     network nothing but a reconnect.
 *  2. Anyone can be one. `wrangler deploy` puts a relay on a Cloudflare
 *     account in about a minute; `npm run node` puts one on any machine
 *     with a port. Cloudflare is used because it is fast and free at the
 *     edge, never because it is required.
 *  3. The relay list is gossiped, not published. It travels inside the
 *     encrypted channel, signed by nodes that have actually reached the
 *     relay. Blocking the website does not block the directory.
 *  4. Several transports, tried in parallel. WebSocket, WebRTC data
 *     channels through any signalling relay, HTTP long-poll for networks
 *     that only pass port 443 request/response traffic, and a local
 *     BroadcastChannel so tabs on one machine mesh with no network at all.
 *  5. Out-of-band bootstrap. A peer ticket is a short signed string. It
 *     travels by QR code, chat message, printed slip, anything. One
 *     working ticket from anyone reachable re-joins you to the whole
 *     network.
 */

import { canonicalBytes, fromHex, toHex, type Hex } from './bytes.ts';
import { tag } from './hash.ts';
import { sign, verify } from './keys.ts';
import { b32decode, b32encode, utf8 } from './bytes.ts';

export type Transport = 'ws' | 'webrtc' | 'http' | 'local';

export interface RelayDescriptor {
  /** Transport-specific address: wss://…, https://…, or a channel name. */
  url: string;
  transport: Transport;
  /** Optional: the relay's own node key, when it runs a full node too. */
  nodeId?: Hex;
  /** Advertised region, purely a latency hint. */
  region?: string;
  /** Unix ms this descriptor was last observed working, by the signer. */
  lastSeen: number;
  /** Signer's key and signature over the descriptor. */
  by?: Hex;
  sig?: Hex;
}

export interface RelayHealth {
  url: string;
  ok: number;
  fail: number;
  lastOkAt: number;
  lastFailAt: number;
  rttMs: number;
}

function descriptorPreimage(d: RelayDescriptor): Uint8Array {
  const { url, transport, nodeId, region, lastSeen } = d;
  return tag('face/relay/v1', canonicalBytes({ url, transport, nodeId, region, lastSeen }));
}

export function signRelay(d: RelayDescriptor, seed: Uint8Array, publicKey: Uint8Array): RelayDescriptor {
  const base: RelayDescriptor = { ...d, by: undefined, sig: undefined };
  return { ...base, by: toHex(publicKey), sig: toHex(sign(seed, descriptorPreimage(base))) };
}

export function verifyRelay(d: RelayDescriptor): boolean {
  if (!d.by || !d.sig) return false;
  const base: RelayDescriptor = { ...d, by: undefined, sig: undefined };
  return verify(d.by, descriptorPreimage(base), d.sig);
}

/* --------------------------- peer tickets --------------------------- */

/**
 * A ticket is everything a stranded node needs to rejoin, small enough for
 * a QR code and readable enough to dictate over a phone call.
 */
export interface PeerTicket {
  relays: RelayDescriptor[];
  chain: string;
  issued: number;
}

export function encodeTicket(ticket: PeerTicket): string {
  const body = b32encode(utf8(JSON.stringify(ticket)));
  const check = b32encode(tag('face/ticket/v1', utf8(JSON.stringify(ticket))).slice(0, 3));
  return `FACE-TICKET-${check}-${(body.match(/.{1,40}/g) || []).join('-')}`;
}

export function decodeTicket(text: string): PeerTicket {
  const cleaned = text.trim().toUpperCase();
  if (!cleaned.startsWith('FACE-TICKET-')) throw new Error('not a FACE peer ticket');
  const parts = cleaned.slice('FACE-TICKET-'.length).split('-').filter(Boolean);
  const check = parts.shift();
  const json = new TextDecoder().decode(b32decode(parts.join('')));
  const ticket = JSON.parse(json) as PeerTicket;
  if (b32encode(tag('face/ticket/v1', utf8(JSON.stringify(ticket))).slice(0, 3)) !== check) {
    throw new Error('ticket checksum failed - it was copied incompletely');
  }
  return ticket;
}

/* -------------------------- relay directory ------------------------- */

/** Seeds are a starting point, not an authority. The directory replaces
 *  them with whatever is actually reachable within a few minutes of uptime. */
export const DEFAULT_SEED_RELAYS: RelayDescriptor[] = [
  { url: 'wss://facewallet.net/relay', transport: 'ws', region: 'edge', lastSeen: 0 },
  { url: 'https://facewallet.net/poll', transport: 'http', region: 'edge', lastSeen: 0 },
];

export class RelayDirectory {
  private relays = new Map<string, RelayDescriptor>();
  private health = new Map<string, RelayHealth>();
  private maxRelays: number;

  constructor(seeds: RelayDescriptor[] = DEFAULT_SEED_RELAYS, maxRelays = 512) {
    this.maxRelays = maxRelays;
    for (const s of seeds) this.add(s);
  }

  add(d: RelayDescriptor): boolean {
    if (!d?.url || typeof d.url !== 'string' || d.url.length > 512) return false;
    if (!['ws', 'webrtc', 'http', 'local'].includes(d.transport)) return false;
    if (d.by && !verifyRelay(d)) return false; // signed but forged - drop it
    const existing = this.relays.get(d.url);
    if (existing && existing.lastSeen >= d.lastSeen) return false;
    this.relays.set(d.url, d);
    if (!this.health.has(d.url)) {
      this.health.set(d.url, { url: d.url, ok: 0, fail: 0, lastOkAt: 0, lastFailAt: 0, rttMs: 0 });
    }
    this.evict();
    return true;
  }

  merge(list: RelayDescriptor[]): number {
    let added = 0;
    for (const d of list.slice(0, 128)) if (this.add(d)) added++;
    return added;
  }

  all(): RelayDescriptor[] {
    return [...this.relays.values()];
  }

  forget(url: string): void {
    this.relays.delete(url);
    this.health.delete(url);
  }

  recordSuccess(url: string, rttMs: number): void {
    const h = this.health.get(url);
    if (!h) return;
    h.ok++;
    h.lastOkAt = Date.now();
    h.rttMs = h.rttMs ? Math.round(h.rttMs * 0.7 + rttMs * 0.3) : rttMs;
    const d = this.relays.get(url);
    if (d) d.lastSeen = h.lastOkAt;
  }

  recordFailure(url: string): void {
    const h = this.health.get(url);
    if (!h) return;
    h.fail++;
    h.lastFailAt = Date.now();
  }

  healthOf(url: string): RelayHealth | undefined {
    return this.health.get(url);
  }

  /**
   * Pick relays to dial. Deliberately spreads across transports rather
   * than taking the n fastest: an all-WebSocket peer set dies the moment
   * WebSockets are filtered, however fast it was beforehand.
   */
  select(count: number, exclude: Set<string> = new Set()): RelayDescriptor[] {
    const score = (d: RelayDescriptor): number => {
      const h = this.health.get(d.url);
      if (!h) return 1;
      const attempts = h.ok + h.fail;
      const rate = attempts ? (h.ok + 1) / (attempts + 2) : 0.5;
      const cooldown = h.lastFailAt && Date.now() - h.lastFailAt < 30_000 ? 0.2 : 1;
      const latency = h.rttMs ? 1 / (1 + h.rttMs / 250) : 0.6;
      return rate * cooldown * (0.6 + 0.4 * latency);
    };

    const byTransport = new Map<Transport, RelayDescriptor[]>();
    for (const d of this.relays.values()) {
      if (exclude.has(d.url)) continue;
      const list = byTransport.get(d.transport) ?? [];
      list.push(d);
      byTransport.set(d.transport, list);
    }
    for (const list of byTransport.values()) list.sort((a, b) => score(b) - score(a));

    const out: RelayDescriptor[] = [];
    const order: Transport[] = ['ws', 'webrtc', 'http', 'local'];
    let round = 0;
    while (out.length < count && round < 32) {
      let progressed = false;
      for (const t of order) {
        const list = byTransport.get(t);
        if (!list || round >= list.length) continue;
        out.push(list[round]);
        progressed = true;
        if (out.length >= count) break;
      }
      if (!progressed) break;
      round++;
    }
    return out;
  }

  ticket(chain: string, count = 6): PeerTicket {
    return { relays: this.select(count), chain, issued: Date.now() };
  }

  private evict(): void {
    if (this.relays.size <= this.maxRelays) return;
    const sorted = [...this.relays.values()].sort((a, b) => {
      const ha = this.health.get(a.url);
      const hb = this.health.get(b.url);
      return (ha?.lastOkAt ?? a.lastSeen) - (hb?.lastOkAt ?? b.lastSeen);
    });
    for (const d of sorted.slice(0, this.relays.size - this.maxRelays)) this.forget(d.url);
  }
}

/** Extra bootstrap paths, tried when the seed relays are all unreachable. */
export function bootstrapCandidates(chain: string): RelayDescriptor[] {
  const out: RelayDescriptor[] = [];
  // Any origin serving the wallet is, by construction, also a relay. This
  // is why mirroring the wallet anywhere also mirrors a way back in.
  const loc = (globalThis as { location?: { origin: string; host: string; protocol: string } }).location;
  if (loc?.origin && loc.protocol.startsWith('http')) {
    const wsProto = loc.protocol === 'https:' ? 'wss:' : 'ws:';
    out.push({ url: `${wsProto}//${loc.host}/relay`, transport: 'ws', region: 'origin', lastSeen: 0 });
    out.push({ url: `${loc.origin}/poll`, transport: 'http', region: 'origin', lastSeen: 0 });
  }
  // Tabs and workers on the same machine mesh without touching the network.
  out.push({ url: `face-local-${chain}`, transport: 'local', region: 'local', lastSeen: 0 });
  return out;
}

export function ticketFingerprint(t: PeerTicket): string {
  return toHex(tag('face/ticket/fp/v1', canonicalBytes(t)).slice(0, 4));
}

export function decodeNodeId(hex: Hex): Uint8Array {
  return fromHex(hex);
}
